import React from "react";
import { Card, Form, Tab, Table, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { Awesome } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { ModalDelete } from "@components/Modals";
import { CommonCard, ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function AccountScreen() {
    const { t } = useTranslation();
    const params = useParams();

    const header = t("Аккаунты");
    return (
        <DefaultLayout>{params.id ? <Edit id={params.id} header={header} /> : <List header={header} />}</DefaultLayout>
    );
}

const List = ({ header }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:account:list"));

    if (!apiData) return;

    const headerTools = (
        <Link to={url_reverse("admin:account:edit", { acc_id: "add" })} className="btn btn-sm btn-dark">
            <Awesome type="plus-circle" /> {t("Добавить")}
        </Link>
    );

    return (
        <ListingCard header={header} headerTools={headerTools}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Логин")}</th>
                        <th>{t("Имя")}</th>
                        <th>{t("Дата последнего входа")}</th>
                        <th>{t("Суперпользователь")}</th>
                        <th>{t("Модератор платформы")}</th>
                        <th>{t("Техподдержка")}</th>
                        <th>{t("Суперпользователь (read only)")}</th>
                        <th>{t("PR модератор")}</th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((obj) => (
                        <tr key={obj.id}>
                            <td>
                                <Link to={url_reverse("admin:account:edit", { acc_id: obj.id })}>{obj.login}</Link>
                            </td>
                            <td>{obj.name}</td>
                            <td>{obj.date_visit}</td>
                            <td>{obj.is_super && <Awesome type="check" color="green" />}</td>
                            <td>{obj.moderator && <Awesome type="check" color="green" />}</td>
                            <td>{obj.support && <Awesome type="check" color="green" />}</td>
                            <td>{obj.read_catalog && <Awesome type="check" color="green" />}</td>
                            <td>{obj.pr_moderator && <Awesome type="check" color="green" />}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ListingCard>
    );
};

const Edit = ({ id, header }) => {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(url_reverse("api:account:edit", { acc_id: id }));
    const onFormSubmit = useSubmit();
    const navigate = useNavigate();

    if (!apiData) return;

    return (
        <>
            <CommonCard header={header}>
                <Form
                    onSubmit={(ev) =>
                        onFormSubmit(ev, {
                            onOK: (resp) => navigate(url_reverse("admin:account:edit", { acc_id: resp.id })),
                        })
                    }
                >
                    <DevForm.Field prefix="main" name="login" />
                    <DevForm.Field prefix="main" name="name" />
                    <DevForm.Field prefix="main" name="date_visit" />
                    <DevForm.Field prefix="main" name="is_super" />
                    <DevForm.Field prefix="main" name="support" />
                    <DevForm.Field prefix="main" name="moderator" />
                    {apiData.forms_data?.main.data.moderator && <DevForm.Field prefix="main" name="comment_as_blog" />}
                    <DevForm.Field prefix="main" name="pr_moderator" />
                    <DevForm.Field prefix="main" name="read_catalog" />
                    <DevForm.Button variant="primary" type="submit" name="save">
                        {t("Сохранить")}
                    </DevForm.Button>
                </Form>
            </CommonCard>
            {apiData.tech_perms?.filter((p) => apiData.forms_data.main.data[p]).length === 0 && (
                <Card className="mt-4">
                    <Card.Header>
                        <a
                            target="_blank"
                            href="https://documentation.vkplay.ru/f2p_vkp/f2p_manage_accs_vkp"
                            className="float-right"
                        >
                            {t("О правах доступа")}
                        </a>
                    </Card.Header>
                    <Card.Body>
                        <ModalDelete />
                        <Tabs defaultActiveKey="devs" transition={false} className="react-bootstrap-tabs-fix">
                            <Tab eventKey="devs" title={t("Доступные компании")}>
                                <PermTable
                                    acc_id={id}
                                    apiData={apiData}
                                    setApiData={setApiData}
                                    entity_type="developer"
                                />
                            </Tab>
                            <Tab eventKey="games" title={t("Доступные игры")}>
                                <PermTable acc_id={id} apiData={apiData} setApiData={setApiData} entity_type="game" />
                            </Tab>
                        </Tabs>
                    </Card.Body>
                </Card>
            )}
        </>
    );
};

const PermTable = ({ acc_id, entity_type, apiData, setApiData }) => {
    const { t } = useTranslation();
    const { setDelData } = useAppContext();

    const object_list = entity_type === "developer" ? apiData.dev_permissions : apiData.game_permissions;

    return (
        <Table hover responsive className="dmg-table">
            <thead>
                <tr>
                    <th>{entity_type === "developer" ? t("Компания") : t("Игра")}</th>
                    <th>{t("Владелец")}</th>
                    <th>{t("Разработчик")}</th>
                    <th>{t("Билд-менеджер")}</th>
                    <th>{t("Аналитик")}</th>
                    <th>{t("Тестировщик")}</th>
                    <th>{t("Комьюнити-менеджер")}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {object_list?.map((o) => {
                    return (
                        <tr key={`${entity_type}${o.id}`}>
                            <td>
                                {entity_type === "developer" ? (
                                    <a target="_blank" href={url_reverse("devs:accounts", { dev_id: o.developer.id })}>
                                        {o.developer.name}
                                    </a>
                                ) : (
                                    <Link to={url_reverse("games:account:list", { game_id: o.game_id })}>
                                        {o.game.base_locale_object.name}
                                    </Link>
                                )}
                            </td>
                            <td>
                                <DevForm.Checkbox checked={o.can_own || false} disabled={true} />
                            </td>
                            <td>
                                <DevForm.Checkbox checked={o.can_edit || false} disabled={true} />
                            </td>
                            <td>
                                <DevForm.Checkbox checked={o.can_distribution || false} disabled={true} />
                            </td>
                            <td>
                                <DevForm.Checkbox checked={o.can_statistics || false} disabled={true} />
                            </td>
                            <td>
                                <DevForm.Checkbox checked={true} disabled />
                            </td>
                            <td>
                                <DevForm.Checkbox checked={o.can_smm || false} disabled={true} />
                            </td>
                            <td className="dmg-table-row-tools">
                                <Awesome
                                    type="trash-o"
                                    color="red"
                                    onClick={() =>
                                        setDelData({
                                            url: url_reverse("api:account:perm_delete", {
                                                acc_id,
                                                entity_type,
                                                entity_id: entity_type === "developer" ? o.developer_id : o.game_id,
                                            }),
                                            afterRespOK: (resp) => {
                                                const newApiData = { ...apiData };
                                                if (entity_type === "developer") {
                                                    newApiData.dev_permissions = newApiData.dev_permissions.filter(
                                                        (dp) => resp.id !== dp.developer_id
                                                    );
                                                } else {
                                                    newApiData.game_permissions = newApiData.game_permissions.filter(
                                                        (gp) => resp.id !== gp.game_id
                                                    );
                                                }
                                                setApiData(newApiData);
                                            },
                                        })
                                    }
                                />
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );
};
